import * as THREE from 'three/webgpu';
import type {RawPointerEventPayload} from '../../viewer/moudles/EventManager';
import type {EntitySelectionManager} from '../EntitySelectionManager';
import type {SmartDimensionVec2} from '../../sketch/dimension/command/SketchSmartDimensionTypes';
import {isSketchContour} from '../../mesh/meshProxy/SketchContour';
import {SketchEntitySelectionHandler} from './SketchEntitySelectionHandler';

/**
 * 智能尺寸模式专用选择处理器。
 *
 * 职责：
 * 1. 薄层委托：拾取结果与指针平面坐标全部转发给 `SketchSmartDimensionController`，
 *    多阶段选择状态机不落在选择层（与 Trim 模式同范式）；
 * 2. 禁用框选与实体拖拽（尺寸命令期间点击/拖动不得移动几何）；
 * 3. 左键命中实体 → 收集引用；未命中且有预览候选 → 放置；右键 → 锁定/解锁。
 */
export class SketchDimensionEntitySelectionHandler extends SketchEntitySelectionHandler {
    /** 指针平面交点复用向量。 */
    private readonly pointerIntersection = new THREE.Vector3();

    /**
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
        this.setDragCapabilityConfig({
            pointDragEnabled: false,
            lineDragEnabled: false,
            contourDragEnabled: false,
        });
    }

    /** @inheritdoc */
    protected override isBoxSelectionEnabled(): boolean {
        return false;
    }

    /**
     * 剔除着色轮廓候选。
     *
     * 说明：轮廓是"区域填充显示"语义，不是可标注实体，智能尺寸拿到它只能判为未命中。
     * 而填充区域会覆盖到实体边线内侧，导致点圆/矩形边线稍微偏内就被轮廓挡掉、
     * 表现为"选中边线无效"。这里在候选集阶段就排除，让射线自然落到真实实体上。
     *
     * @param targets 原始候选。
     * @returns 不含轮廓的候选。
     */
    private excludeContourTargets(targets: THREE.Object3D[]): THREE.Object3D[] {
        /**
         * 路径长度命令例外：封闭轮廓是"整圈选择"的入口（SW——点上色轮廓
         * 自动选中封闭图形全部边），必须保留在候选集里由控制器展开。
         */
        if (this.resolveController()?.isPathLengthCommand() === true) {
            return targets;
        }

        return targets.filter((target) => !isSketchContour(target));
    }

    /** @inheritdoc */
    protected override collectClickSelectionTargets(): THREE.Object3D[] {
        return this.excludeContourTargets(super.collectClickSelectionTargets());
    }

    /** @inheritdoc */
    protected override collectHoverSelectionTargets(): THREE.Object3D[] {
        return this.excludeContourTargets(super.collectHoverSelectionTargets());
    }

    /** 解析智能尺寸控制器。 */
    private resolveController() {
        return this.entitySelectionManager.renderer.sketchSmartDimensionController;
    }

    /**
     * 把屏幕坐标换算为活动草图参考面局部坐标。
     *
     * @param clientX 屏幕 X。
     * @param clientY 屏幕 Y。
     * @returns 局部坐标；无活动草图或射线与平面无交点时返回 null。
     */
    private resolvePointerLocal(clientX: number, clientY: number): SmartDimensionVec2 | null {
        const renderer = this.entitySelectionManager.renderer;
        const container = renderer.container;
        const camera = renderer.camera;
        const referencePlane = renderer.sketchGroupManager?.activeSketchGroup?.referencePlane ?? null;

        if (!container || !camera || !referencePlane) {
            return null;
        }

        const rect = container.getBoundingClientRect();

        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouseNdc, camera);

        if (!this.raycaster.ray.intersectPlane(referencePlane, this.pointerIntersection)) {
            return null;
        }

        const local = referencePlane.worldToLocal(this.pointerIntersection);

        return [local.x, local.y];
    }

    /** @inheritdoc */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button === 2) {
            // 右键：预览候选锁定/解锁；被命令消费时不再进入其他右键链路。
            this.resolveController()?.handleRightClick();
            return;
        }
        void payload;
    }

    /** @inheritdoc */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        const hoveredPick = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectHoverSelectionTargets(),
        );

        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));

        const pointerLocal = this.resolvePointerLocal(payload.clientX, payload.clientY);

        if (pointerLocal) {
            this.resolveController()?.handlePointerMove(pointerLocal);
            this.entitySelectionManager.renderer.syncSmartDimensionPreview(pointerLocal);
        }
    }

    /** @inheritdoc */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /** @inheritdoc */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        const controller = this.resolveController();

        if (!controller) {
            return;
        }

        /**
         * 智能尺寸拾取直接使用原始命中，不做槽口整体提升
         * （`resolveClickSelectionPick` 会把槽口子边/端弧提升为槽口 Group，
         * 那样标注拿到的既非线也非弧而被拒绝）。对齐 SOLIDWORKS：点端部半圆
         * 出半径尺寸、点上下边线出边线尺寸——需要保留光标下的真实子成员。
         */
        const picked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );
        const modifiers = {ctrlKey: payload.ctrlKey, shiftKey: payload.shiftKey};

        if (picked && controller.handleEntityClick(picked.object, modifiers)) {
            this.entitySelectionManager.renderer.syncSmartDimensionPreview(
                this.resolvePointerLocal(payload.clientX, payload.clientY),
            );
            return;
        }

        // 未命中可收集实体：预览态下为放置点击（Shift 放置对称角 = 完整角）。
        const pointerLocal = this.resolvePointerLocal(payload.clientX, payload.clientY);

        if (pointerLocal) {
            controller.handlePlacementClick(pointerLocal, modifiers);
            this.entitySelectionManager.renderer.syncSmartDimensionPreview(pointerLocal);
        }
    }
}
